import { useState } from 'react';
import { getPractice } from '../lib/catalog';
import { createRoutine, DEFAULT_ROUTINE_ID, getRoutine, lastRoutineId, loadRoutines, rememberRoutine, routineSeconds } from '../lib/routines';
import { currentStreak, loadHistory, timeBand, type TimeBand } from '../lib/storage';
import { formatLength } from '../lib/timeline';
import { beginRun } from '../lib/sessionStore';
import type { Practice } from '../types';
import { Icon } from '../ui/Icon';
import { SunGlow } from '../ui/SunGlow';
import { RoutineCard } from './RoutineCard';
import { RoutineEditor } from './RoutineEditor';
import styles from './screens.module.css';

const GREETING: Record<TimeBand, string> = { morning: 'Good morning', day: 'Good afternoon', evening: 'Good evening' };

export const Today = ({ onPlayRoutine, onExplore }: { onPlayRoutine: (id: string) => void; onExplore: () => void }) => {
  const band = timeBand();
  const streak = currentStreak(loadHistory());
  const [routines, setRoutines] = useState(loadRoutines);
  const [selectedId, setSelectedId] = useState(() => (getRoutine(lastRoutineId()) ? lastRoutineId() : DEFAULT_ROUTINE_ID));
  const [editing, setEditing] = useState(false);

  const reload = () => setRoutines(loadRoutines());
  const selected = routines.find(r => r.id === selectedId) ?? routines[0];
  const others = routines.filter(r => r.id !== selected.id);

  const begin = () => {
    const queue = selected.items.map(getPractice).filter((p): p is Practice => Boolean(p));
    if (!queue.length) return;
    rememberRoutine(selected.id);
    beginRun(queue, selected); // inside the tap so audio unlocks
    onPlayRoutine(selected.id);
  };

  const create = () => {
    const r = createRoutine();
    reload();
    setSelectedId(r.id);
    setEditing(true);
  };

  return (
    <>
      <SunGlow position="page" />
      <main className={`screen ${styles.today}`}>
        <header className={styles.greeting}>
          <h1>{GREETING[band]}</h1>
          {streak > 0 && <p className={styles.streak}>Day {streak}</p>}
        </header>

        <section className={`${styles.todayCard} surface`} aria-label="Your session">
          <RoutineCard key={selected.id + selected.items.join()} routine={selected} onBegin={begin} onEdit={() => setEditing(true)} />
        </section>

        <section aria-label="Other sessions">
          <h3 className={styles.sectionTitle}>Other sessions</h3>
          <div className={styles.alsoGrid}>
            {others.map(r => (
              <button key={r.id} className={`${styles.alsoTile} surface`} onClick={() => setSelectedId(r.id)}>
                <Icon name="lotus" size={24} />
                <span className={styles.alsoName}>{r.title}</span>
                <span className="eyebrow-soft">
                  {r.items.length} practices, {formatLength(routineSeconds(r))}
                </span>
              </button>
            ))}
            <button className={`${styles.alsoTile} ${styles.createTile} surface`} onClick={create}>
              <span className={styles.plus} aria-hidden="true">+</span>
              <span className={styles.alsoName}>Create your own</span>
              <span className="eyebrow-soft">Pick practices, set the order</span>
            </button>
          </div>
          <button className={`text-button ${styles.oneLink}`} onClick={onExplore}>
            Or play just one practice
          </button>
        </section>
      </main>

      {editing && (
        <RoutineEditor
          key={selected.id}
          routine={selected}
          onClose={() => setEditing(false)}
          onChanged={reload}
          onDeleted={() => {
            setEditing(false);
            setSelectedId(DEFAULT_ROUTINE_ID);
            reload();
          }}
        />
      )}
    </>
  );
};
